/**
 * Manpower P&L — Vendors listing + CRUD, as thin config over the generic <ResourceListing>.
 * List + City filter + Add (POST) + Edit (PATCH) + soft-Delete (DELETE), contract-typed.
 * Honest-types null-handling: city_name can be null (e.g. __UNASSIGNED__) → guarded with '—'.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import {
	ResourceListing,
	Column,
	RowAction,
	StatusPill,
} from '../../../components/manpower/ResourceListing';
import { useVendorData } from '../hooks/useVendorData';
import { useVendorMutations } from '../hooks/useVendorMutations';
import { useCities } from '../hooks/useCities';
import { VendorModal } from './VendorModal';
import type { Vendor } from '../../../services/manpowerVendorsApi';

export const VendorListing: React.FC = () => {
	const navigate = useNavigate();
	const [cityId, setCityId] = useState<number | ''>('');
	const [status, setStatus] = useState<'' | 'active' | 'inactive'>(''); // rows never show status — this does
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState<Vendor | null>(null);
	const [toast, setToast] = useState<string | null>(null);
	const [confirmDelete, setConfirmDelete] = useState<Vendor | null>(null);

	const { data, isLoading, error } = useVendorData(cityId === '' ? undefined : { cityId });
	const { data: cities = [] } = useCities();
	const { remove } = useVendorMutations();
	const vendors: Vendor[] = (data?.data ?? []).filter(v => (status ? v.status === status : true));

	const showToast = (msg: string) => {
		setToast(msg);
		window.setTimeout(() => setToast(null), 3000);
	};

	const handleDelete = async () => {
		if (!confirmDelete?.id) return;
		try {
			await remove.mutateAsync(confirmDelete.id);
			showToast('Vendor deleted');
		} catch (e) {
			showToast(e instanceof Error ? e.message : 'Delete failed');
		} finally {
			setConfirmDelete(null);
		}
	};

	const columns: Column<Vendor>[] = [
		{ header: 'ID', render: v => v.id ?? '—' },
		{ header: 'Name', render: v => v.name ?? '—' },
		{ header: 'Status', render: v => <StatusPill text={v.status} tone={v.status === 'active' ? 'green' : 'gray'} /> },
		{ header: 'City', render: v => v.city_name ?? '—' },
		{ header: 'Workers', align: 'right', render: v => v.worker_count ?? 0 },
		{ header: 'Vehicles', align: 'right', render: v => v.vehicle_count ?? 0 },
	];

	const actions: RowAction<Vendor>[] = [
		{
			label: 'Edit',
			colorClass: 'text-blue-600',
			icon: <Pencil className='h-4 w-4' />,
			onClick: v => {
				setEditing(v);
				setModalOpen(true);
			},
		},
		{ label: 'Delete', colorClass: 'text-red-600', icon: <Trash2 className='h-4 w-4' />, onClick: v => setConfirmDelete(v) },
	];

	return (
		<ResourceListing<Vendor>
			title='Vendors'
			subtitle='Manpower P&L — active vendors'
			phoneTable
			// null-city system vendors (Infinity Box, __UNASSIGNED__) error on the
			// backend city-scope check — not real vendors, so not drillable.
			// ponytail: FE guard for the demo; the proper fix is a backend scope fix.
			onRowClick={v => v.id != null && v.city_id != null && navigate(`/manpower/vendors/${v.id}/workers`)}
			addLabel='+ Add Vendor'
			onAdd={() => {
				setEditing(null);
				setModalOpen(true);
			}}
			filter={[
				{
					label: 'City',
					value: cityId,
					onChange: v => setCityId(v as number | ''),
					allLabel: 'All cities',
					options: cities.map(c => ({ id: c.id, label: c.name })),
				},
				{
					label: 'Status',
					value: status,
					onChange: v => setStatus(v as '' | 'active' | 'inactive'),
					allLabel: 'All statuses',
					options: [
						{ id: 'active', label: 'Active' },
						{ id: 'inactive', label: 'Inactive' },
					],
				},
			]}
			columns={columns}
			rows={vendors}
			rowKey={(v, i) => v.id ?? i}
			actions={actions}
			isLoading={isLoading}
			error={error}
			toast={toast}
		>
			<VendorModal
				open={modalOpen}
				editing={editing}
				onClose={() => setModalOpen(false)}
				onSuccess={showToast}
			/>

			{confirmDelete && (
				<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
					<div className='w-full max-w-sm rounded-lg bg-white p-5 shadow-xl'>
						<h3 className='text-base font-semibold text-gray-900'>Delete vendor?</h3>
						<p className='mt-2 text-sm text-gray-600'>
							This soft-deletes <span className='font-medium'>{confirmDelete.name ?? 'this vendor'}</span>{' '}
							(sets status to inactive).
						</p>
						<div className='mt-5 flex justify-end gap-2'>
							<button
								type='button'
								onClick={() => setConfirmDelete(null)}
								className='rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50'
							>
								Cancel
							</button>
							<button
								type='button'
								onClick={handleDelete}
								disabled={remove.isPending}
								className='rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50'
							>
								{remove.isPending ? 'Deleting…' : 'Delete'}
							</button>
						</div>
					</div>
				</div>
			)}
		</ResourceListing>
	);
};

export default VendorListing;
